import { createTheme } from '@mui/material/styles';
import { axisClasses, chartsGridClasses } from '@mui/x-charts-pro';

// eslint-disable-next-line import/no-mutable-exports
let theme = createTheme({
  // https://mui.com/customization/default-theme/#explore
  palette: {
    common: {
      black: '#282828',
      white: '#FFFFFF',
    },
    text: {
      primary: '#282828',
      secondary: '#6D6D6D',
      disabled: '#B6B6B6',
    },
    background: {
      paper: '#FFFFFF',
      default: '#FAF8F7',
    },
    primary: {
      main: '#3E577D',
      light: '#EDF1FA', // light background color
      light2: '#DAE3F5', // hover background color
      transparent: 'rgba(62, 87, 125, 0.2)', // primary.main with 20% opacity
    },
    button: {
      textHover: '#4874CD',
      focusBorder: '#AEC6EB',
      navBackground: '#E8EBF3',
      navBackgroundHover: '#E4E7EB',
    },
    success: {
      main: '#00642B',
      light: '#E9F7EF',
    },
    info: {
      main: '#00327E',
      light: '#EBEEF2',
    },
    warning: {
      main: '#765800',
      light: '#FDF5DF',
      light2: '#FFEEBF',
    },
    error: {
      main: '#970000',
      light: '#FCEBEB',
      light2: '#FFDEDE',
    },
    grey: {
      75: '#FAF8F7',
      100: '#F2F4F6',
      150: '#EEEEEE',
      200: '#E4E7EB',
      300: '#DDDDDD',
      500: '#B6B6B6',
      600: '#6D6D6D',
    },
    code: {
      white: '#DDD',
      textBlue: '#B3CEE5',
      postBlue: '#2980B9',
      okGreen: '#27AE60',
      green: '#c9e2bc',
      orange: '#e5b7a5',
      error: '#FF7F7F',
    },
    charts: {
      green: {
        fill: 'hsla(151, 26.00%, 35.50%, 0.20)',
        stroke: '#43725B',
      },
      purple: {
        fill: 'rgba(135, 104, 207, 0.20)',
        stroke: '#8768CF',
      },
      blue: {
        fill: 'hsla(216, 45.50%, 59.00%, 0.20)',
        stroke: '#678DC6',
        background: 'rgba(103, 141, 198, 0.10)',
      },
    },
  },
  typography: {
    fontFamily: '"inter-regular", "Arial Narrow", sans-serif',
    h1: {
      fontFamily: '"inter-semibold", "Arial Narrow", sans-serif',
      fontSize: '1.25rem', // 20px
      lineHeight: 'normal',
      letterSpacing: 'normal',
      textTransform: 'capitalize',
    },
    h2: {
      fontFamily: '"inter-semibold", "Arial Narrow", sans-serif',
      fontSize: '1rem', // 16px
      lineHeight: '0.875rem',
      letterSpacing: 'normal',
    },
    h3: {
      fontFamily: '"inter-medium", "Arial Narrow", sans-serif',
      fontSize: '0.875rem', // 14px
      lineHeight: '0.875rem',
      letterSpacing: 'normal',
    },
    body1: {
      fontFamily: 'inherit',
      fontSize: '0.875rem', // 14px
      lineHeight: '1.25rem',
      letterSpacing: 'normal',
    },
    body2: {
      fontFamily: 'inherit',
      fontSize: '.75rem', // 12px
      lineHeight: '1rem',
      letterSpacing: 'normal',
    },
    monospace: {
      fontFamily: '"ibm-plex-mono-medium", "Lucida Console", Monaco, monospace',
      fontSize: '0.875rem', // 14px
      lineHeight: '1.5rem',
      letterSpacing: 'normal',
    },
    bold: {
      fontFamily: '"inter-medium", "Arial Narrow", sans-serif',
    },
    button: {
      fontFamily: '"inter-medium", "Arial Narrow", sans-serif',
      fontSize: '0.875rem', // 14px
    },
    caption: {
      fontFamily: '"inter-regular", "Arial Narrow", sans-serif',
      fontSize: '.625rem', // 10px
      lineHeight: 'normal',
      letterSpacing: 'normal',
      color: '#6D6D6D',
    },
    // TODO: convert everything to disabled TextField - then don't need this?
    snippet: {
      fontSize: '.775rem',
      border: '1px solid #E4E7EB',
      backgroundColor: '#F2F4F6',
      borderRadius: '8px',
      padding: '10.5px 12px',
      width: '360px',
      boxSizing: 'border-box',
    },
    h4: {
      fontFamily: '"inter-medium", "Arial Narrow", sans-serif',
      fontSize: '1rem',
      lineHeight: 'normal',
      letterSpacing: 'normal',
    },
    subtitle2: { // TODO: check if this is still needed
      fontFamily: '"inter-medium", "Arial Narrow", sans-serif',
      fontSize: '.875rem',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      color: '#6D6D6D',
    },
    // TODO: remove usage of this, use a defined variant directly, ie: 'caption'
    fontSizes: {
      xs: '.75rem',
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 200,
      md: 700,
      lg: 993,
      xl: 1280,
      iPadPro: 1366,
    },
  },
  border: {
    default: '1px solid #E4E7EB', // gray 200
    radius: '8px',
    radius2: '4px',
    boxShadow: '0px 0px 4px rgba(142, 142, 142, 0.25)',
    boxShadow2: '0px 0px 4px rgba(142, 142, 142, 0.5)',
    boxShadow3: '0px 4px 4px 0px #E8E8E840',
  },
  scrollShadows: {
    overflowY: 'auto',
    flexGrow: 1,
    // Top and bottom scroll shadows to indicate scrollable content
    // Usage example: Connect Customizations -> CustomizationsGroup
    // Ref: https://css-tricks.com/books/greatest-css-tricks/scroll-shadows/
    // Breakdown of the following background property:
    // background gradient 1: top shadow cover
    // background gradient 2: bottom shadow cover
    // background gradient 3: top shadow
    // background gradient 4: bottom shadow
    background: 'linear-gradient(white 30%,rgba(255, 255, 255, 0)) center top,linear-gradient(rgba(255, 255, 255, 0),white 70%) center bottom,linear-gradient(rgba(142, 142, 142, 0.15),white) center top,linear-gradient(white,rgba(142, 142, 142, 0.15)) center bottom',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100% 40px, 100% 40px, 100% 8px, 100% 8px',
    backgroundAttachment: 'local, local, scroll, scroll',
    zIndex: 1,
  },
  styledScrollbar: {
    /* width */
    '::-webkit-scrollbar': {
      width: '16px',
    },
    /* track */
    '::-webkit-scrollbar-track': {
      // needed to cover scroll shadows
      // instead of using background color
      marginTop: '-5px',
      marginBottom: '-5px',
    },
    /* handle */
    '::-webkit-scrollbar-thumb': {
      background: '#E4E7EB',
      borderRadius: '8px',
      border: '5px solid #FFF',
    },
    /* handle on hover */
    '::-webkit-scrollbar-thumb:hover': {
      background: '#B6B6B6',
    },
  },
  width: {
    content: '1152px',
    paragraph: '70ch',
    input: '360px',
    sidebar: {
      expanded: '230px',
      collapsed: '53px',
    },
  },
  height: {
    topbar: '64px',
  },
  zIndex: {
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    tooltip: 1400,
    snackbar: 1500,
    popover: 1600,
    commandbar: {
      launcher: 1000,
      nudges: 1101, // needs to sit above AppBar but below Modal
      checklist: 1002,
      helphub: 1003,
    },
  },
});

// Theme composition: using theme options to define other options
// https://mui.com/customization/theming/#api
theme = createTheme(theme, {
  components: {
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&.MuiPaper-root': {
            boxShadow: 'none',
          },
          '&:not(.icon-right) .MuiAccordionSummary-content': {
            order: 2,
          },
          '&:not(.icon-right) .MuiAccordionSummary-expandIconWrapper': {
            order: 1,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.common.black,
          padding: theme.spacing(2),
          borderRadius: theme.border.radius,
          alignItems: 'center',
          width: 'max-content',
        },
        outlinedError: {
          backgroundColor: theme.palette.error.light,
          borderColor: theme.palette.error.main,
        },
        outlinedWarning: {
          backgroundColor: theme.palette.warning.light,
          borderColor: theme.palette.warning.main,
        },
        outlinedInfo: {
          backgroundColor: `${theme.palette.info.light} !important`, // needed when used inside Drawer
          borderColor: theme.palette.info.main,
        },
        outlinedSuccess: {
          backgroundColor: theme.palette.success.light,
          borderColor: theme.palette.success.main,
        },
        filledError: {
          backgroundColor: theme.palette.error.light,
        },
        filledWarning: {
          backgroundColor: theme.palette.warning.light,
        },
        filledInfo: {
          backgroundColor: '#80C0CE33',
        },
        filledSuccess: {
          backgroundColor: 'rgba(39, 174, 96, 0.2)',
        },
        action: {
          marginRight: '0px',
        },
        icon: {
          padding: '0px',
          alignItems: 'center',
        },
        message: {
          padding: '0px',
          alignItems: 'center',
          width: '100%',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          border: `1px solid ${theme.palette.primary.main}`,
          boxShadow: 'none',
          borderRadius: theme.border.radius,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          marginTop: '-6px', // sliding upward to cover the select button's bottom rounded corners
        },
        option: {
          fontSize: theme.typography.body2.fontSize,
        },
        endAdornment: {
          button: {
            border: 'none',
          },
        },
      },
    },
    MuiBarElement: {
      styleOverrides: {
        root: {
          strokeWidth: 2,
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiButton: {
      // defaultProps: {
      //   variant: 'outlined',
      // },
      styleOverrides: {
        root: {
          padding: '10.5 12px',
          borderRadius: theme.border.radius,
          textTransform: 'none',
          transition:
            'color 0.25s ease, background-color 0.25s ease, border 0.25s ease',
          '&:focus, &:focus-visible, &:active': {
            outline: `2px solid ${theme.palette.button.focusBorder}`,
          },
          '&.Mui-disabled': {
            opacity: 0.5,
          },
          '& .MuiButton-icon .spinner': {
            fontSize: '14px',
            marginBottom: '-5px',
          },
        },
        outlined: {
          color: theme.palette.primary.main,
          border: `1px solid ${theme.palette.primary.main}`,
          backgroundColor: theme.palette.primary.light,
          '&:hover': {
            backgroundColor: theme.palette.primary.light2,
          },
          '&.Mui-disabled': {
            color: theme.palette.primary.main,
            border: `1px solid ${theme.palette.primary.main}`,
            backgroundColor: theme.palette.primary.light,
          },
        },
        outlinedSecondary: {
          color: theme.palette.primary.main,
          backgroundColor: theme.palette.common.white,
          border: `1px solid ${theme.palette.grey[200]}`,
          '&:hover': {
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.grey[100],
            border: `1px solid ${theme.palette.grey[200]}`,
          },
          '&:focus, &:focus-visible, &:active': {
            backgroundColor: theme.palette.common.white,
          },
          '&.Mui-disabled': {
            color: theme.palette.primary.main,
            backgroundColor: theme.palette.common.white,
            border: `1px solid ${theme.palette.grey[200]}`,
          },
        },
        outlinedError: {
          color: theme.palette.error.main,
          border: `1px solid ${theme.palette.error.main}`,
          backgroundColor: theme.palette.error.light,
          '&:hover': {
            backgroundColor: theme.palette.error.light2,
          },
          '&.Mui-disabled': {
            color: theme.palette.error.main,
            border: `1px solid ${theme.palette.error.main}`,
            backgroundColor: theme.palette.error.light,
          },
          '&:focus, &:focus-visible, &:active': {
            color: theme.palette.error.main,
          },
        },
        outlinedWarning: {
          color: theme.palette.warning.main,
          border: `1px solid ${theme.palette.warning.main}`,
          backgroundColor: theme.palette.warning.light,
          '&:hover': {
            backgroundColor: theme.palette.warning.light2,
          },
          '&.Mui-disabled': {
            color: theme.palette.warning.main,
            border: `1px solid ${theme.palette.warning.main}`,
            backgroundColor: theme.palette.warning.light,
          },
          '&:focus, &:focus-visible, &:active': {
            color: theme.palette.warning.main,
          },
        },
        text: {
          color: theme.palette.primary.main,
          backgroundColor: 'transparent',
          border: '2px solid transparent',
          '&:hover': {
            color: theme.palette.button.textHover,
            backgroundColor: 'transparent',
            textDecoration: 'none',
          },
          '&:focus, &:focus-visible, &:active': {
            backgroundColor: 'transparent',
            textDecoration: 'none',
          },
          '&.Mui-disabled': {
            color: theme.palette.primary.main,
            backgroundColor: 'transparent',
          },
        },
        unstyled: {
          border: 'none',
          fontFamily: 'inherit',
          fontSize: 'inherit',
          padding: '0px',
          margin: '0px',
          '&:hover, &:focus, &:focus-visible, &:active': {
            backgroundColor: 'transparent',
            border: 'none',
          },
        },
        sizeSmall: {
          fontSize: theme.typography.body2.fontSize,
          padding: '7.5px 12px',
        },
        contained: {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
          fontFamily: theme.typography.fontFamily,
          boxShadow: 'none',
          border: `2px solid ${theme.palette.primary.main}`,
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.button.textHover,
            boxShadow: 'none',
          },
          '&:focus, &:focus-visible, &:active': {
            backgroundColor: theme.palette.button.textHover,
            boxShadow: 'none',
          },
          '&.Mui-disabled': {
            color: theme.palette.common.white,
            backgroundColor: theme.palette.primary.main,
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: theme.border.radius,
          fontSize: '1rem',
          padding: '8px',
          color: theme.palette.primary.main,

          '.MuiSvgIcon-root': {
            fontSize: '1.25rem',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '.checkbox-icon': {
            width: '20px',
            height: '20px',
            borderRadius: '4px',
            border: `1px solid ${theme.palette.grey[500]}`,

            '&_checked': {
              border: `1px solid ${theme.palette.primary.main}`,
              backgroundColor: theme.palette.primary.main,
              position: 'relative',

              '&::after': {
                position: 'absolute',
                content: '""',
                border: '1px solid #fff',
                opacity: 1,
                '-webkit-transform': 'rotate(45deg) scale(1.2)',
                '-ms-transform': 'rotate(45deg) scale(1.2)',
                transform: 'rotate(45deg) scale(1.2)',
                left: '8px',
                top: '3px',
                width: '3px',
                height: '10px',
                borderWidth: '0 1.5px 1.5px 0',
                borderRadius: '0px',
              },
            },
          },
          '&.Mui-disabled': {
            '.checkbox-icon': {
              border: `1px solid ${theme.palette.grey[200]}`,
            },
          },
        },
      },
    },
    MuiChartsAxis: {
      styleOverrides: {
        root: {
          [`& .${axisClasses.label}`]: {
            fill: theme.palette.grey[600],

            '& text': {
              fontSize: `${theme.typography.fontSizes.xs} !important`,
            },
          },
          [`& .${axisClasses.line}`]: {
            stroke: theme.palette.grey[200],
            strokeWidth: 2,
          },
          [`&.${axisClasses.directionY} .${axisClasses.line}`]: {
            display: 'none',
          },
          [`&.${axisClasses.directionY} .${axisClasses.label}`]: {
            transform: `translateX(-${theme.spacing(6)})`,
          },
          [`&.${axisClasses.directionX} .${axisClasses.label}`]: {
            transform: `translateY(${theme.spacing(1)})`,
          },
          [`& .${axisClasses.tickLabel}`]: {
            // fontFamily: theme.typography.bold.fontFamily,
            fontSize: theme.typography.fontSizes.xs,
            fill: theme.palette.grey[600],
          },
          [`& .${axisClasses.tick}`]: {
            display: 'none',
          },
        },
      },
    },
    MuiChartsGrid: {
      styleOverrides: {
        root: {
          [`& .${chartsGridClasses.line}`]: {
            strokeDasharray: '5 3',
            strokeWidth: 1,
            stroke: theme.palette.grey[200],
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: {
          '.MuiDialog-paperFullScreen': {
            '.MuiDialogContent-root': {
              padding: '0px',
            },
          },
          '& .MuiDialog-paperWidthSm': {
            maxWidth: '600px', // overriding 'sm' size default of 700px
          },
          '& .MuiDialogTitle-root': {
            ...theme.typography.h2,
          },
          '& .MuiDialogContent-root': {
            padding: '24px',
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: theme.palette.grey[200],
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          '& .MuiPaper-root': {
            backgroundColor: theme.palette.background.paper,
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          borderColor: theme.border.default,
          borderRadius: theme.border.radius,
        },
        columnHeader: {
          color: theme.palette.grey[600],
          fontFamily: theme.typography.bold.fontFamily,
          padding: theme.spacing(2, 3),
          '&.p-none': {
            padding: 0,
          },

          '&.p-l-none': {
            paddingLeft: 0,
          },

          '&:focus, &:focus-within': {
            outline: 'none',
          },
        },
        cell: {
          padding: theme.spacing(2, 3),
          color: theme.palette.grey[600],
          '&:focus, :focus-within': {
            outline: 'none',
          },
          '&.p-none': {
            padding: 0,
          },

          '&.p-l-none': {
            paddingLeft: 0,
          },
          a: {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            fontFamily: theme.typography.bold.fontFamily,
            color: theme.palette.text.primary,

            '&:hover': {
              color: theme.palette.primary.main,
              textDecoration: 'none',
            },
          },
        },
        menu: {
          backgroundColor: theme.palette.background.paper,
        },
        panel: {
          backgroundColor: theme.palette.background.paper,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          '&.MuiInputLabel-root': {
            position: 'relative',
            color: theme.palette.text.primary,
            fontFamily: theme.typography.bold.fontFamily,
            fontSize: theme.typography.body2.fontSize,
            transform: 'none',
            marginBottom: theme.spacing(1),
          },

          '& .MuiFormLabel-asterisk': {
            display: 'none',
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          '&:before': {
            borderBottom: `1px solid ${theme.palette.grey[200]}`,
          },

          '&:hover:before': {
            borderBottom: `1px solid ${theme.palette.grey[500]} !important`,
          },

          '&:after': {
            borderBottom: `1px solid ${theme.palette.primary.main}`,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          padding: '0',
          width: theme.width.input,
          fontSize: theme.typography.body2.fontSize,
          borderRadius: theme.border.radius,
          input: {
            padding: '10.5px 12px',
            borderRadius: theme.border.radius,
          },
          textarea: {
            padding: '10.5px 12px',
            borderRadius: theme.border.radius,
          },
          '&.MuiInputBase-fullWidth': {
            width: '100%',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.grey[200],
            top: 0,
            legend: {
              display: 'none',
            },
          },
          '&.Mui-disabled, &.Mui-readOnly': {
            backgroundColor: theme.palette.grey[100],
            input: {
              '-webkit-text-fill-color': theme.palette.text.primary,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.grey[200],
            },
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
          '&.Mui-disabled:hover .MuiOutlinedInput-notchedOutline, &.Mui-readOnly:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.grey[200],
          },
          '&.MuiInputBase-adornedStart, &.MuiInputBase-adornedEnd': {
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1.5),
          },
        },
      },
    },
    MuiLineElement: {
      styleOverrides: {
        root: {
          strokeWidth: 1,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          fontFamily: theme.typography.bold.fontFamily,
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'none',
            color: theme.palette.button.textHover,

            '& .MuiTypography-root': {
              textDecoration: 'none',
            },
          },
          '&:active, &:focus, &:focus-visible': {
            textDecoration: 'underline',
            color: theme.palette.button.textHover,
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.background.paper,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&:first-child': {
            paddingTop: '0px',
          },
          '&:last-child': {
            paddingBottom: '0px',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          marginTop: '0px',
          marginBottom: '0px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: 0,
          borderRadius: theme.border.radius,

          '&:hover': {
            backgroundColor: theme.palette.button.navBackground,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          '&.MuiPopover-paper': {
            border: theme.border.default,
            boxShadow: 'none',
            /* styled scrollbar */
            '::-webkit-scrollbar': {
              width: '14px',
            },
            '::-webkit-scrollbar-track': {
              marginTop: '0',
              marginBottom: '0',
            },
            '::-webkit-scrollbar-thumb': {
              background: '#E4E7EB',
              borderRadius: '8px',
              border: '4px solid #FFF',
            },
            '::-webkit-scrollbar-thumb:hover': {
              background: '#B6B6B6',
            },
          },
          '&.MuiDialog-paper': {
            borderRadius: theme.border.radius,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          padding: '10.5px 12px',
        },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          '& .MuiStepLabel-root .MuiStepLabel-label': {
            fontFamily: theme.typography.bold.fontFamily,
            '&.Mui-active': {
              fontWeight: 400,
            },
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: '42px',
          height: '26px',
          padding: 0,
          margin: '8px',
          '& .MuiSwitch-switchBase': {
            padding: 0,
            margin: '2px',
            transitionDuration: '300ms',
            '&.Mui-checked': {
              transform: 'translateX(16px)',
              color: '#fff',
              '& + .MuiSwitch-track': {
                backgroundColor: theme.palette.primary.main,
                opacity: 1,
                border: 0,
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
              },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
              color: theme.palette.primary.main,
              border: '6px solid #fff',
            },
            '&.Mui-disabled .MuiSwitch-thumb': {
              color: theme.palette.grey[100],
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: 0.7,
            },
          },
          '& .MuiSwitch-thumb': {
            boxSizing: 'border-box',
            width: 22,
            height: 22,
          },
          '& .MuiSwitch-track': {
            borderRadius: 26 / 2,
            backgroundColor: '#E9E9EA',
            opacity: 1,
            transitionDuration: '300ms',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.common.black,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: '16px 0px',
          margin: '0px 24px',
          fontFamily: theme.typography.bold.fontFamily,
          fontSize: '1.125rem',
          textTransform: 'none',
          lineHeight: 'normal',
          minHeight: 'auto',
          minWidth: 'auto',
          '@media (min-width: 0px)': {
            minWidth: 'auto',
          },
          '&.Mui-selected': {
            color: theme.palette.common.black,
          },
        },
        textColorInherit: {
          color: theme.palette.grey[600],
          opacity: 1,
          '&.Mui-selected': {
            color: theme.palette.common.black,
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.common.white,
          borderRadius: theme.border.radius,
          border: theme.border.default,
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          'tr:last-child td': {
            borderBottom: 'none',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:not(.MuiTableRow-head):hover': {
            backgroundColor: theme.palette.grey[100],
          },
          '&.disabled': {
            '& .MuiTableCell-root': {
              color: theme.palette.text.disabled,
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: theme.spacing(2, 3),
          fontSize: theme.typography.body2.fontSize,
          color: theme.palette.text.secondary,
          a: {
            color: theme.palette.text.primary,
            fontFamily: theme.typography.bold.fontFamily,
            textDecoration: 'none',
            '&:hover': {
              color: `${theme.palette.primary.main} !important`,
              textDecoration: 'none',
            },
          },
        },
        head: {
          color: theme.palette.grey[600],
          fontFamily: theme.typography.bold.fontFamily,
        },
        body: {
          fontSize: theme.typography.body2.fontSize,
          color: theme.palette.text.secondary,
          '&:focus, :focus-within': {
            outline: 'none',
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: theme.palette.common.black,
          fontSize: theme.typography.body2.fontSize,
          padding: theme.spacing(1, 2),
        },
        arrow: {
          color: theme.palette.text.primary,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          padding: theme.spacing(0.25),
          fontFamily: theme.typography.bold.fontFamily,
          fontSize: '0.875rem',
          variants: [
            {
              props: { variant: 'success' },
              style: {
                color: theme.palette.success.main,
                backgroundColor: theme.palette.success.light,
                border: 'none',
              },
            },
            {
              props: { variant: 'disabled' },
              style: {
                border: 'none',
                color: theme.palette.grey[600],
                backgroundColor: theme.palette.grey[100],
              },
            },
            {
              props: { variant: 'primary' },
              style: {
                border: 'none',
                color: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.light,
              },
            },
          ],
        },
      },
    },
  },
});

export default theme;
